import type { Criteria, Pagination, PropertySort } from '@elastic/eui';
import {
  EuiButton,
  EuiFieldSearch,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiInMemoryTable,
} from '@elastic/eui';
import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { AssetsSplashScreen, SplashScreenMode } from './assets_splash_screen';
import { AddToPortfolioModal, AssetDetailsFlyout, AssetIdLabel, PortfolioHintsPanel } from '../../components';
import { usePageMeta } from '../../hooks';
import type { AsyncData, FundConflatedAsset, SerializedFundConflatedAsset } from '../../model';
import { deserializeFundConflatedAsset, PAGINATION, QUERY_PARAM_NAME } from '../../model';
import { PageContext } from '../../page_container';

export function AssetsPage() {
  usePageMeta('Assets');

  const [searchParams, setSearchParams] = useSearchParams();
  const onLoadQuery = useMemo(() => new URLSearchParams(window.location.search).get(QUERY_PARAM_NAME) ?? '', []);

  const [assetSearchQuery, setAssetSearchQuery] = useState<string>(onLoadQuery);
  const [searchState, setSearchState] = useState<
    AsyncData<FundConflatedAsset[] | null, { previousData: FundConflatedAsset[] }>
  >(onLoadQuery ? { status: 'pending' } : { status: 'succeeded', data: null });

  const { settings, parameters } = useContext(PageContext);
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: settings.defaultPageSize,
    pageSizeOptions: PAGINATION.pageSizeOptions,
    totalItemCount: 0,
  });

  const [assetToInspect, setAssetToInspect] = useState<FundConflatedAsset | null>(null);
  const toggleAssetDetails = useCallback(
    (asset?: FundConflatedAsset) => {
      window.location.hash = asset ? `#${asset.id}` : '';
      setAssetToInspect(asset === assetToInspect ? null : (asset ?? null));
    },
    [assetToInspect],
  );

  const [sorting, setSorting] = useState<{ sort: PropertySort }>({ sort: { field: '', direction: 'asc' } });

  const [assetToAddToPortfolio, setAssetToAddToPortfolio] = useState<FundConflatedAsset | null>(null);
  const toggleAddToPortfolioModal = useCallback((asset: FundConflatedAsset | null = null) => {
    setAssetToAddToPortfolio(asset);
  }, []);

  const performSearch = () => {
    if (!assetSearchQuery) {
      setPagination((pagination) => ({ ...pagination, pageIndex: 0, totalItemCount: 0 }));
      setSearchState({ status: 'succeeded', data: [] });
      return;
    }

    setSearchState({
      status: 'pending',
      state: searchState.status === 'succeeded' && searchState.data ? { previousData: searchState.data } : undefined,
    });

    axios.post('/api/assets/search', { domicile: parameters.domicileCode, query: assetSearchQuery }).then(
      ({ data: assets }: { data: SerializedFundConflatedAsset[] }) => {
        setSearchState({ status: 'succeeded', data: assets.map(deserializeFundConflatedAsset) });
        setPagination((pagination) => ({ ...pagination, pageIndex: 0, totalItemCount: assets.length }));
      },
      () => {
        setPagination((pagination) => ({ ...pagination, pageIndex: 0, totalItemCount: 0 }));
        setSearchState({ status: 'succeeded', data: [] });
      },
    );
  };

  useEffect(() => {
    if (!parameters.synced || !onLoadQuery) {
      return;
    }

    axios.post('/api/assets/search', { domicile: parameters.domicileCode, query: onLoadQuery }).then(
      ({ data: assets }: { data: SerializedFundConflatedAsset[] }) => {
        setSearchState({ status: 'succeeded', data: assets.map(deserializeFundConflatedAsset) });
        setPagination((pagination) => ({ ...pagination, pageIndex: 0, totalItemCount: assets.length }));
      },
      () => {
        setSearchState({ status: 'succeeded', data: [] });
        setPagination((pagination) => ({ ...pagination, pageIndex: 0, totalItemCount: 0 }));
      },
    );
  }, [parameters, onLoadQuery]);

  const assetDetailsFlyout = assetToInspect ? (
    <AssetDetailsFlyout
      assetSourceId={assetToInspect.sourceId}
      assetName={assetToInspect.name}
      onClose={toggleAssetDetails}
    />
  ) : null;

  const addAssetToPortfolioModal = assetToAddToPortfolio ? (
    <AddToPortfolioModal item={assetToAddToPortfolio} onClose={() => toggleAddToPortfolioModal(null)} />
  ) : null;

  let mainContent;
  if (searchState.status === 'pending' && (!searchState.state || searchState.state.previousData.length === 0)) {
    mainContent = <AssetsSplashScreen mode={SplashScreenMode.Loading} />;
  } else if (searchState.status === 'succeeded' && !searchState.data) {
    mainContent = <AssetsSplashScreen mode={SplashScreenMode.Default} />;
  } else if (searchState.status === 'succeeded' && searchState.data?.length === 0) {
    mainContent = <AssetsSplashScreen mode={SplashScreenMode.NoData} />;
  } else {
    const isLoading = searchState.status === 'pending';
    const items =
      (searchState.status === 'succeeded'
        ? searchState.data
        : searchState.status === 'pending'
          ? searchState.state?.previousData
          : []) ?? [];
    mainContent = (
      <EuiInMemoryTable
        pagination={pagination}
        allowNeutralSort={true}
        sorting={sorting}
        onTableChange={({ page, sort }: Criteria<FundConflatedAsset>) => {
          setPagination({
            ...pagination,
            pageIndex: page?.index ?? 0,
            pageSize: page?.size ?? settings.defaultPageSize,
          });

          if (typeof sort?.field === 'string') {
            setSorting({ sort });
          }
        }}
        loading={isLoading}
        items={items}
        itemId="id"
        columns={[
          {
            name: 'ID',
            field: 'id',
            width: '145px',
            render: (_, asset) => <AssetIdLabel id={asset.id} sourceId={asset.sourceId} />,
          },
          {
            name: 'Asset Class',
            field: 'id',
            width: '105px',
            render: (_, asset) => parameters.assetClasses.get(asset.funds[0]?.[1]?.assetClass)?.name ?? '-',
          },
          {
            name: 'Name',
            field: 'name',
            sortable: true,
            footer: <strong>{items.length} assets</strong>,
          },
          {
            name: 'Actions',
            field: 'fund',
            width: '75px',
            actions: [
              {
                name: 'View asset details',
                description: 'View asset details',
                icon: 'inspect',
                type: 'icon',
                enabled: () => !isLoading,
                onClick: toggleAssetDetails,
              },
              {
                name: 'Add to portfolio',
                description: 'Add to portfolio',
                icon: 'plusInCircle',
                type: 'icon',
                enabled: () => !isLoading,
                onClick: (asset) => toggleAddToPortfolioModal(asset),
              },
            ],
          },
        ]}
      />
    );
  }

  const portfolioHintsPanel =
    searchState.status === 'succeeded' && (searchState.data?.length ?? 0) > 0 ? (
      <EuiFlexItem grow={false}>
        <PortfolioHintsPanel />
      </EuiFlexItem>
    ) : null;

  return (
    <>
      <EuiFlexGroup direction={'column'} gutterSize={'s'} style={{ height: '100%' }}>
        <EuiFlexItem grow={false}>
          <EuiForm>
            <EuiFormRow fullWidth>
              <EuiFlexGroup gutterSize={'s'}>
                <EuiFlexItem>
                  <EuiFieldSearch
                    fullWidth
                    placeholder="Search asset by name, ISIN or ticker"
                    value={assetSearchQuery}
                    isClearable={true}
                    isLoading={searchState.status === 'pending'}
                    onChange={(e) => {
                      const query = e.target.value;
                      setAssetSearchQuery(query);

                      if (query) {
                        searchParams.set(QUERY_PARAM_NAME, query);
                      } else {
                        searchParams.delete(QUERY_PARAM_NAME);
                      }

                      setSearchParams(searchParams);
                    }}
                    onSearch={(query: string) => {
                      if (!query) {
                        setSearchState({ status: 'succeeded', data: null });
                      } else {
                        performSearch();
                      }
                    }}
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton iconType="search" onClick={() => performSearch()}>
                    Search
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFormRow>
          </EuiForm>
        </EuiFlexItem>
        {portfolioHintsPanel}
        <EuiFlexItem>{mainContent}</EuiFlexItem>
      </EuiFlexGroup>
      {assetDetailsFlyout}
      {addAssetToPortfolioModal}
    </>
  );
}
